import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormControlLabel, formControlLabelClasses, Radio, RadioGroup as MuiRadioGroup, styled } from '@mui/material';

import RadioChecked from '@/assets/icons/radio-checked.svg?react';
import RadioEmpty from '@/assets/icons/radio-empty.svg?react';
import { DropdownSelectOption } from '@/components/fields/DropdownSelect';
import withFormControl from '@/hoc/withFormControl';

interface RadioGroupProps {
  options: Omit<DropdownSelectOption, 'Icon'>[];
  value: string;
  disabled?: boolean;
  name?: string;
}

const RadioGroupField = ({ options = [], value: selected, disabled, name }: RadioGroupProps) => {
  const form = useFormContext();
  const handleOnChange = useCallback(
    (_e: React.ChangeEvent<HTMLInputElement>, value: string) => {
      form.setValue(name!, value, { shouldDirty: true });
    },
    [form, name]
  );

  return (
    <MuiRadioGroup value={selected} onChange={handleOnChange}>
      {options.map(option => (
        <StyledControlLabel
          key={option.value}
          checked={option.value === selected}
          value={option.value}
          label={option.label}
          disabled={disabled}
          control={<StyledRadio checkedIcon={<RadioChecked />} icon={<RadioEmpty />} />}
        />
      ))}
    </MuiRadioGroup>
  );
};

const RadioGroup = withFormControl<string, string, RadioGroupProps>(RadioGroupField);

export default RadioGroup;

const StyledControlLabel = styled(FormControlLabel)(({ theme: { typography, palette }, checked }) => ({
  marginLeft: 0,

  [`& .${formControlLabelClasses.label}`]: {
    ...typography.body3,
    fontWeight: typography.fontWeightMedium,
    color: checked ? palette.text.primary : palette.grey[700]
  },

  [`&.${formControlLabelClasses.disabled}`]: {
    color: palette.grey[500]
  }
}));

const StyledRadio = styled(Radio)(({ theme: { spacing, palette } }) => ({
  padding: 0,
  color: palette.grey[800],

  [`& svg`]: {
    width: spacing(4),
    height: spacing(4)
  }
}));
